import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Send, X, ShieldCheck, Sparkles, AlertCircle } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function Bubble({ msg }) {
  const isUser = msg.role === "user";
  const isAdmin = msg.role === "admin";
  const isSystem = msg.role === "system";
  const align = isUser ? "items-end" : "items-start";
  const colorClass = isUser
    ? "bg-[#1E3A8A] text-white"
    : isAdmin
    ? "bg-[#DC2626]/15 border border-[#DC2626]/40 text-white"
    : isSystem
    ? "bg-white/5 text-white/65 italic"
    : "bg-[#FBBF24]/10 border border-[#FBBF24]/30 text-white";
  return (
    <div className={`flex flex-col ${align}`}>
      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1 font-bold">
        {isUser ? "You" : isAdmin ? "ZFD Admin" : isSystem ? "System" : "Zen"}
      </div>
      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${colorClass}`}>
        {msg.content}
      </div>
    </div>
  );
}

export default function ChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState("active");
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [unread, setUnread] = useState(false);
  const scrollRef = useRef(null);

  // Find most recent session on mount
  useEffect(() => {
    if (!user) return;
    api.get("/chat/mine").then((r) => {
      const latest = r.data?.[0];
      if (latest) {
        setSessionId(latest.id);
        setStatus(latest.status);
        if (latest.unread_for_user) setUnread(true);
      }
    }).catch(() => {});
  }, [user]);

  // Poll for new admin messages every 12s while widget closed
  useEffect(() => {
    if (!sessionId) return;
    const tick = async () => {
      try {
        const { data } = await api.get("/chat/mine");
        const cur = data.find((s) => s.id === sessionId);
        if (cur?.unread_for_user && !open) setUnread(true);
        if (open) {
          const { data: detail } = await api.get(`/chat/sessions/${sessionId}`);
          setMessages(detail.messages);
          setStatus(detail.session.status);
        }
      } catch {}
    };
    const i = setInterval(tick, 12000);
    return () => clearInterval(i);
  }, [sessionId, open]);

  useEffect(() => {
    if (open && sessionId) {
      api.get(`/chat/sessions/${sessionId}`).then((r) => { setMessages(r.data.messages); setStatus(r.data.session.status); setUnread(false); }).catch(() => {});
    }
    if (open && !sessionId) {
      setMessages([{ id: "welcome", role: "assistant", content: `Hi ${user?.full_name?.split(" ")[0] || "Partner"}, I'm Zen — your commerce coach. Ask me about products, orders, marketing content, affiliate links, commissions, or anything else about your Zenith business. If I can't help, I'll loop in our support team.`, created_at: new Date().toISOString() }]);
    }
  }, [open, sessionId, user]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  if (!user || user.role === "admin") return null;

  const send = async () => {
    if (!input.trim() || busy) return;
    const text = input.trim();
    setInput("");
    setBusy(true);
    // Optimistic user bubble
    setMessages((m) => [...m, { id: "tmp-" + Date.now(), role: "user", content: text, created_at: new Date().toISOString() }]);
    try {
      const { data } = await api.post("/chat/message", { message: text, session_id: sessionId });
      if (!sessionId) setSessionId(data.session_id);
      setStatus(data.status);
      // Reload full message list to reconcile
      const { data: detail } = await api.get(`/chat/sessions/${data.session_id}`);
      setMessages(detail.messages);
    } catch (e) {
      setMessages((m) => [...m, { id: "err-" + Date.now(), role: "system", content: formatErr(e), created_at: new Date().toISOString() }]);
    } finally { setBusy(false); }
  };

  const escalate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/chat/escalate", { message: "I'd like to talk to a human.", session_id: sessionId });
      if (!sessionId) setSessionId(data.session_id);
      setStatus(data.status);
      const { data: detail } = await api.get(`/chat/sessions/${data.session_id}`);
      setMessages(detail.messages);
    } catch (e) {
      setMessages((m) => [...m, { id: "err-" + Date.now(), role: "system", content: formatErr(e), created_at: new Date().toISOString() }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={() => { setOpen(!open); setUnread(false); }}
        data-testid="chat-widget-toggle"
        className={`fixed bottom-20 md:bottom-5 left-5 z-40 w-14 h-14 rounded-full flex items-center justify-center shadow-xl transition-all ${open ? "bg-white/10 text-white" : "bg-[#FBBF24] text-[#0F2042] hover:scale-105"}`}
        aria-label="Open chat"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        {!open && unread && <span className="absolute top-1 right-1 w-3 h-3 rounded-full bg-[#DC2626] border-2 border-[#0F2042]" data-testid="chat-unread-dot" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-40 md:bottom-24 left-5 z-40 w-[92vw] max-w-md h-[65vh] md:h-[70vh] max-h-[640px] glass-card gold-border flex flex-col overflow-hidden"
            data-testid="chat-widget-panel"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-white/10 bg-[#0F2042]/70 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#FBBF24] flex items-center justify-center text-[#0F2042] font-display font-black">Z</div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-sm flex items-center gap-2">
                  Zen <Sparkles size={12} className="text-[#FBBF24]" />
                </div>
                <div className="text-[10px] uppercase tracking-widest text-white/45">
                  {status === "escalated" ? (
                    <span className="text-[#FCA5A5] flex items-center gap-1"><ShieldCheck size={10} /> Admin queue</span>
                  ) : status === "resolved" ? (
                    <span className="text-emerald-300">Resolved</span>
                  ) : (
                    <span>AI Assistant · ZFD Support</span>
                  )}
                </div>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gradient-to-b from-transparent to-black/20" data-testid="chat-messages-list">
              {messages.map((m) => <Bubble key={m.id} msg={m} />)}
              {busy && (
                <div className="flex items-center gap-2 text-xs text-white/45" data-testid="chat-typing">
                  <span className="inline-block w-1.5 h-1.5 bg-[#FBBF24] rounded-full animate-pulse" />
                  <span className="inline-block w-1.5 h-1.5 bg-[#FBBF24] rounded-full animate-pulse" style={{ animationDelay: "0.15s" }} />
                  <span className="inline-block w-1.5 h-1.5 bg-[#FBBF24] rounded-full animate-pulse" style={{ animationDelay: "0.3s" }} />
                  Zen is typing…
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="border-t border-white/10 p-3 bg-[#0F2042]/60">
              {status !== "escalated" && (
                <button
                  onClick={escalate}
                  disabled={busy}
                  className="text-[11px] text-white/55 hover:text-[#FBBF24] mb-2 flex items-center gap-1 disabled:opacity-50"
                  data-testid="chat-escalate-btn"
                >
                  <AlertCircle size={12} /> Talk to a human instead
                </button>
              )}
              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={status === "escalated" ? "Add more details for the admin…" : "Ask Zen anything…"}
                  className="flex-1 zfd-input"
                  data-testid="chat-input"
                  disabled={busy}
                />
                <button
                  type="submit"
                  disabled={busy || !input.trim()}
                  className="bg-[#FBBF24] text-[#0F2042] hover:bg-yellow-300 px-4 py-2 rounded-md font-bold disabled:opacity-40 flex items-center"
                  data-testid="chat-send"
                >
                  <Send size={16} />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
